import {
  MenuItem,
  Stack,
  SxProps,
  TextField,
  TextFieldProps,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
  Control,
  UseFormTrigger,
  UseFormGetValues,
  UseFormResetField,
  UseFormSetValue,
  Path,
  useController,
  RegisterOptions,
} from 'react-hook-form'

import {
  EMPTY_OPTION,
  FLOOR_OPTIONS,
  HEAT_TYPE_OPTIONS,
  LAND_CONDITION_OPTIONS,
  POOL_OPTIONS,
} from 'store/CreatePropertyAdSlice/constants'
import Label, { LabelProps } from './Label'
import { FormValues } from '../'

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 400,
    },
  },
  sx: {
    '& .MuiButtonBase-root.MuiMenuItem-root.Mui-selected': {
      bgcolor: 'primary.main',
    },
  },
}
type OptionsType =
  | typeof FLOOR_OPTIONS
  | typeof HEAT_TYPE_OPTIONS
  | typeof POOL_OPTIONS
  | typeof LAND_CONDITION_OPTIONS

interface ConnectedSelectMultipleProps {
  options: OptionsType
  onChange?: (any) => void
  title?: string
  titleProps?: LabelProps
  label?: string
  placeholder?: string
  translateTitle?: boolean
  translateLabel?: boolean
  textfieldProps?: TextFieldProps
  sxProps?: SxProps
  control: Control<FormValues>
  trigger?: UseFormTrigger<FormValues>
  getValues?: UseFormGetValues<FormValues>
  resetField?: UseFormResetField<FormValues>
  setValue?: UseFormSetValue<FormValues>
  name: Path<FormValues>
  rules?: RegisterOptions
  formatter?: (value: OptionsType) => string
}

const ConnectedSelectMultipleField = (props: ConnectedSelectMultipleProps) => {
  const {
    options,
    title,
    titleProps,
    label,
    translateTitle = false,
    translateLabel = false,
    sxProps,
    textfieldProps,
    formatter,
    control,
    name,
    rules,
  } = props
  const { t } = useTranslation()

  const {
    field,
    fieldState: { error },
  } = useController<FormValues>({
    name,
    control,
    rules: rules,
    defaultValue: [],
    shouldUnregister: true,
  })

  const labelFallback = label ? label : undefined
  const labelTranslate = label ? t(label) : label

  return (
    <Stack spacing={2}>
      {title ? (
        <Label
          required={true}
          label={title}
          translateLabel={translateTitle}
          {...titleProps}
        />
      ) : null}
      <TextField
        select
        SelectProps={{
          multiple: true,
          displayEmpty: true,
          renderValue: (selected: typeof options) => {
            if (selected.length === 0) {
              return <>{t(EMPTY_OPTION.label)}</>
            }
            if (formatter) {
              return formatter(selected)
            }
            return selected.join(', ')
          },
          MenuProps: MenuProps,
        }}
        variant="outlined"
        fullWidth
        {...field}
        error={Boolean(error)}
        helperText={error?.message}
        label={translateLabel ? labelTranslate : labelFallback}
        sx={{
          ...sxProps,
        }}
        {...textfieldProps}
      >
        <MenuItem disabled value={EMPTY_OPTION.value}>
          {t(EMPTY_OPTION.label)}
        </MenuItem>
        {options.map((option) => (
          <MenuItem key={option?.label} value={option?.value}>
            {t(option?.label)}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  )
}

export default ConnectedSelectMultipleField
